"use client";

import * as React from "react";
import { LucideIcon, ChevronRight } from "lucide-react";
import { cn } from "@/lib/utils";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";

// ============================================================================
// FeatureCard - Rich visual cards with image backgrounds
// ============================================================================

export interface FeatureCardProps {
  /** Card title */
  title: string;
  /** Card description */
  description?: string;
  /** Background image URL */
  imageUrl?: string;
  /** Fallback gradient if no image */
  gradientFrom?: string;
  gradientVia?: string;
  gradientTo?: string;
  /** Badges to display at top */
  badges?: Array<{
    text: string;
    icon?: LucideIcon;
    position: "left" | "right";
    variant?: "default" | "premium" | "success" | "info";
  }>;
  /** Stats to show */
  stats?: Array<{
    icon?: LucideIcon | string;
    value: string | number;
    label?: string;
  }>;
  /** Progress indicator */
  progress?: {
    value: number;
    max: number;
    label?: string;
  };
  /** Primary action */
  action?: {
    label: string;
    href?: string;
    onClick?: () => void;
  };
  /** Click handler for entire card */
  onClick?: () => void;
  /** Additional className */
  className?: string;
  /** Height of the card */
  height?: "sm" | "md" | "lg";
  /** Children for custom content */
  children?: React.ReactNode;
}

const heightStyles = {
  sm: "h-64",
  md: "h-80",
  lg: "h-96",
};

const badgeVariants = {
  default: "bg-gray-800/90 text-white border-gray-600",
  premium: "bg-yellow-500/90 text-black border-yellow-400",
  success: "bg-emerald-500/90 text-white border-emerald-400",
  info: "bg-blue-500/90 text-white border-blue-400",
};

export function FeatureCard({
  title,
  description,
  imageUrl,
  gradientFrom = "emerald-600",
  gradientVia = "teal-600",
  gradientTo = "cyan-700",
  badges,
  stats,
  progress,
  action,
  onClick,
  className,
  height = "md",
  children,
}: FeatureCardProps) {
  const Wrapper = onClick ? "button" : "div";

  return (
    <Wrapper
      onClick={onClick}
      className={cn(
        "group relative overflow-hidden rounded-2xl shadow-lg",
        "hover:shadow-xl transition-all duration-500 hover:scale-[1.02]",
        onClick && "cursor-pointer text-left w-full",
        heightStyles[height],
        className
      )}
    >
      {/* Background */}
      <div className="absolute inset-0">
        {imageUrl ? (
          <>
            <img
              src={imageUrl}
              alt=""
              className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />
          </>
        ) : (
          <>
            <div className={`absolute inset-0 bg-gradient-to-br from-${gradientFrom} via-${gradientVia} to-${gradientTo}`} />
            <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-black/20" />
            {/* Decorative pattern */}
            <div className="absolute inset-0 opacity-10" style={{
              backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
            }} />
          </>
        )}
      </div>

      {/* Badges */}
      {badges && badges.length > 0 && (
        <>
          {/* Left badges */}
          <div className="absolute top-4 left-4 z-10 flex flex-col gap-2">
            {badges
              .filter((b) => b.position === "left")
              .map((badge, idx) => (
                <Badge
                  key={idx}
                  className={cn(
                    "backdrop-blur-sm font-semibold",
                    badgeVariants[badge.variant || "default"]
                  )}
                >
                  {badge.icon && <badge.icon className="h-3 w-3 mr-1" />}
                  {badge.text}
                </Badge>
              ))}
          </div>
          {/* Right badges */}
          <div className="absolute top-4 right-4 z-10 flex flex-col gap-2">
            {badges
              .filter((b) => b.position === "right")
              .map((badge, idx) => (
                <Badge
                  key={idx}
                  className={cn(
                    "backdrop-blur-sm font-semibold",
                    badgeVariants[badge.variant || "default"]
                  )}
                >
                  {badge.icon && <badge.icon className="h-3 w-3 mr-1" />}
                  {badge.text}
                </Badge>
              ))}
          </div>
        </>
      )}

      {/* Content Overlay */}
      <div className="absolute inset-0 flex flex-col justify-end p-6 text-white">
        {/* Title */}
        <h3 className="text-xl md:text-2xl font-bold mb-2 line-clamp-2 group-hover:text-emerald-300 transition-colors duration-300">
          {title}
        </h3>

        {/* Description */}
        {description && (
          <p className="text-sm text-gray-200 line-clamp-2 mb-4 leading-relaxed">
            {description}
          </p>
        )}

        {/* Stats Row */}
        {stats && stats.length > 0 && (
          <div className="flex items-center gap-3 mb-4 flex-wrap">
            {stats.map((stat, idx) => (
              <div
                key={idx}
                className="flex items-center gap-1 bg-white/20 backdrop-blur-sm rounded-full px-3 py-1 text-sm"
              >
                {typeof stat.icon === "string" ? (
                  <span>{stat.icon}</span>
                ) : stat.icon ? (
                  <stat.icon className="h-4 w-4" />
                ) : null}
                <span className="font-semibold">{stat.value}</span>
                {stat.label && <span className="text-gray-300 text-xs">{stat.label}</span>}
              </div>
            ))}
          </div>
        )}

        {/* Progress */}
        {progress && (
          <div className="mb-4">
            <div className="flex items-center justify-between text-xs text-gray-300 mb-1">
              <span>{progress.label || "Progress"}</span>
              <span>{Math.round((progress.value / progress.max) * 100)}%</span>
            </div>
            <div className="w-full bg-white/30 rounded-full h-2">
              <div
                className="bg-emerald-400 h-2 rounded-full transition-all duration-500"
                style={{ width: `${(progress.value / progress.max) * 100}%` }}
              />
            </div>
          </div>
        )}

        {/* Custom children */}
        {children}

        {/* Action Button */}
        {action && (
          <Button
            asChild={!!action.href}
            onClick={action.onClick}
            className="w-full bg-white/20 backdrop-blur-sm border-white/30 text-white hover:bg-white hover:text-gray-900 transition-all duration-300 font-semibold h-11"
          >
            {action.href ? (
              <a href={action.href}>
                {action.label}
                <ChevronRight className="h-4 w-4 ml-2 group-hover:translate-x-1 transition-transform" />
              </a>
            ) : (
              <>
                {action.label}
                <ChevronRight className="h-4 w-4 ml-2 group-hover:translate-x-1 transition-transform" />
              </>
            )}
          </Button>
        )}
      </div>

      {/* Hover border effect */}
      <div className="absolute inset-0 rounded-2xl border-2 border-transparent opacity-0 group-hover:opacity-50 transition-opacity duration-500 pointer-events-none"
        style={{
          background: "linear-gradient(135deg, #34d399, #3b82f6, #a855f7) border-box",
          WebkitMask: "linear-gradient(#fff 0 0) padding-box, linear-gradient(#fff 0 0)",
          WebkitMaskComposite: "xor",
          maskComposite: "exclude",
        }}
      />
    </Wrapper>
  );
}

// ============================================================================
// FeatureCardGrid - Grid container for feature cards
// ============================================================================

export interface FeatureCardGridProps {
  children: React.ReactNode;
  columns?: 1 | 2 | 3;
  className?: string;
}

export function FeatureCardGrid({
  children,
  columns = 2,
  className,
}: FeatureCardGridProps) {
  const gridCols = {
    1: "grid-cols-1",
    2: "grid-cols-1 md:grid-cols-2",
    3: "grid-cols-1 md:grid-cols-2 lg:grid-cols-3",
  };

  return (
    <div className={cn("grid gap-6", gridCols[columns], className)}>
      {children}
    </div>
  );
}

